import { useEffect, useMemo, useRef, useState } from 'react';
import { useClient } from "urql";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  TextField,
  FormHelperText,
} from '@mui/material';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import AutocompleteMultiSelect from '../AutocompleteMultiSelect';
import FullScreenLoadCircle from '../FullScreenLoadCircle';
import NotificationSettingPostForm from '../NotificationSettingPostForm';
import { NotificationSignalTypes, SignalNoticeTypes, SignalSalesAmountResultType } from 'applications/signal/constants';
import { getSignalBrandSalesAmountQuery, getSignalBrandSalesRateQuery, getBrandShareRateQuery } from "applications/signal/hooks/gqlHooks"
import { useIShopChainsStore } from 'applications/signal/stores/useIShopChainsStore';
import { useGroupCategoriesBrandsStore } from 'applications/signal/stores/useGroupCategoriesBrandsStore';
import { categoryNameConcat, extractLv4Name, filterBrandDataByCategory } from 'applications/signal/utils/dataUtils';
import { NotificationSettingEditFormData, NotificationSettingEditFormSchema } from './schema';
import { areaOptions, channelOptions, genderAgeOptions} from "constants/constants";
import { setGlobalMessage } from 'hooks/useGlobalMessage';

/**
 * 更新処理の場合に使用する型
 */
type NotificationSettingProps = {
  id?: string;
  name?: string;
  signal_type: number;
  notice_type: number;
  active?: boolean;
  categories: string[];
  areas: string[];
  channels: string[];
  gender_ages: string[];
  shop_chains: string[];
  target_days: number;
  target_brand_name: string;
  min_threshold: number;
  max_threshold: number;
};
const OPTIONAL_FILTERS_BY_SIGNAL_TYPE = {
  shopChains: [
    NotificationSignalTypes.SalesAmount.ordinal,
    NotificationSignalTypes.SalesQuantity.ordinal,
  ],
  genderAges: [
    NotificationSignalTypes.SalesRate.ordinal
  ],
};
const DEFAULT_SIGNAL_TYPE = NotificationSignalTypes.ShareRate.ordinal;
export const DEFAULT_NOTICE_TYPE = SignalNoticeTypes[0].id;
const DEFAULT_TARGET_DAYS = 3;

export default function NotificationSettingEditForm({ registeredFormData }: { registeredFormData?: NotificationSettingProps }) {
  const editFormRef = useRef<HTMLDivElement>(null);
  const postFormRef = useRef<HTMLDivElement>(null);
  const urqlClient = useClient();
  const [signalType, setSignalType] = useState<number>(registeredFormData?.signal_type || DEFAULT_SIGNAL_TYPE); // 項目切り替え検知用
  const [brandData, setBrandData] = useState<string[]>([]);
  const [sampleResponse, setSampleResponse] = useState<any>(null);
  const [isLoading, setLoading] = useState(false);
  const minStartDate = dayjs().subtract(3, 'month');
  const defaultStartDate = dayjs().subtract(7, 'day').format("YYYY-MM-DD");

  const signalTypeOptions = useMemo(() => {
    return Object.values(NotificationSignalTypes).map(type => ({
      label: type.label,
      id: type.ordinal,
    }));
  }, []);
  const { data: categoryBrandData, loading: categoryBrandFetching, init: categoryBrandInit } = useGroupCategoriesBrandsStore();
  const { data: ishopChainsData, loading: ishopChainsLoading, init: ishopChainsInit } = useIShopChainsStore();
  useEffect(() => { categoryBrandInit(urqlClient); ishopChainsInit(urqlClient); }, [urqlClient]);
  const categoryData = Array.from(new Set(categoryBrandData?.map(row => categoryNameConcat(row)) || [])).sort();
  useEffect(() => {
    if (categoryBrandData && registeredFormData?.categories) {
      setBrandData(filterBrandDataByCategory(categoryBrandData, registeredFormData.categories));
    }
  }, [categoryBrandData, registeredFormData]);
  const iShopChainsData = Array.from(new Set(ishopChainsData?.map(row => row.name) || []));

  useEffect(() => {
    // sampleResponseが更新されたらフォーム下部までスクロール
    if (sampleResponse) postFormRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [sampleResponse]);
  const onClickRecalculate = () => {
    // フォーム上部までスクロール
    editFormRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }

  const {
    control,
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<NotificationSettingEditFormData>({
    resolver: zodResolver(NotificationSettingEditFormSchema),
    defaultValues: {
      signalType: registeredFormData?.signal_type || DEFAULT_SIGNAL_TYPE,
      categories: registeredFormData?.categories || [],
      areas: registeredFormData?.areas || [],
      channels: registeredFormData?.channels || [],
      genderAges: registeredFormData?.gender_ages || [],
      shopChains: registeredFormData?.shop_chains || [],
      startDate: defaultStartDate,
      targetDays: registeredFormData?.target_days || DEFAULT_TARGET_DAYS,
      targetBrand: registeredFormData?.target_brand_name || "",
      comperetiveBrands: [],
    },
  });

  const isLoadingSomething = useMemo(() =>
    isLoading || categoryBrandFetching || ishopChainsLoading,
    [isLoading, categoryBrandFetching, ishopChainsLoading]
  );

  const onSubmit = async (data: NotificationSettingEditFormData) => {
    setLoading(true);

    const input = {
      start_date: data.startDate,
      target_days: data.targetDays,
      target_brand_name: data.targetBrand,
      comperetive_brand_names: data.comperetiveBrands,
      categories: data.categories.map(item => extractLv4Name(item)),
      areas: data.areas,
      channels: data.channels,
      gender_ages: data.genderAges,
      shop_chains: data.shopChains,
    };
    let query;
    let params: { data: typeof input; amount_type?: string } = { data: input };
    switch (data.signalType) {
      case NotificationSignalTypes.SalesAmount.ordinal:
        query = getSignalBrandSalesAmountQuery;
        params = { ...params, amount_type: SignalSalesAmountResultType.salesAmount };
        break;
      case NotificationSignalTypes.SalesQuantity.ordinal:
        query = getSignalBrandSalesAmountQuery;
        params = { ...params, amount_type: SignalSalesAmountResultType.salesQuantity };
        break;
      case NotificationSignalTypes.SalesRate.ordinal:
        query = getSignalBrandSalesRateQuery;
        break;
      case NotificationSignalTypes.ShareRate.ordinal:
      default:
        query = getBrandShareRateQuery;
        break;
    }

    // onSubmitの中でGraphQLクエリを同期的に実行したいのでhookを使わずに直接クエリを実行
    const { data: result, error: brandShareError } = await urqlClient.query(query, params).toPromise();
    if (brandShareError) {
      console.error("Error fetching brand share rate:", brandShareError);
      setLoading(false);
      setGlobalMessage({
        message: "Signal設定の参考値計算に失敗しました。",
        severity: "error",
      });
    }

    setLoading(false);
    setSampleResponse(result);
  };

  return (
    <>
      {isLoadingSomething &&  <FullScreenLoadCircle />}
      <Box ref={editFormRef} sx={{ maxWidth: 600, mx: "auto" }}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormControl
            fullWidth
            margin="normal"
            error={!!errors.signalType}
          >
            <Controller
              name="signalType"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={signalTypeOptions}
                  value={signalTypeOptions.find(option => option.id === field.value) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.id || DEFAULT_SIGNAL_TYPE);
                    setSignalType(newValue?.id || DEFAULT_SIGNAL_TYPE);
                  }}
                  getOptionLabel={(option) => option.label || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={"シグナルタイプ"}
                      placeholder={"選択または検索"}
                    />
                  )}
                />
              )}
            />
            <FormHelperText>
              {errors.signalType?.message}
            </FormHelperText>
          </FormControl>
          <FormControl
            fullWidth
            margin="normal"
            error={!!errors.categories}
          >
            <Controller
              name="categories"
              control={control}
              render={({ field }) => (
                <AutocompleteMultiSelect
                  label="市場(カテゴリー)"
                  options={categoryData || []}
                  value={field.value || []}
                  onChange={(newValue) => {
                    setBrandData(filterBrandDataByCategory(categoryBrandData || [], newValue));
                    field.onChange(newValue);
                  }}
                />
              )}
            />
            <FormHelperText>
              {errors.categories?.message}
            </FormHelperText>
          </FormControl>
          <FormControl
            fullWidth
            margin="normal"
            error={!!errors.areas}
          >
            <Controller
              name="areas"
              control={control}
              render={({ field }) => (
                <AutocompleteMultiSelect
                  label="エリア"
                  noSelectionLabel="全国"
                  options={areaOptions || []}
                  value={field.value || []}
                  onChange={field.onChange}
                />
              )}
            />
            <FormHelperText>
              {errors.areas?.message}
            </FormHelperText>
          </FormControl>
          <FormControl
            fullWidth
            margin="normal"
            error={!!errors.channels}
          >
            <Controller
              name="channels"
              control={control}
              render={({ field }) => (
                <AutocompleteMultiSelect
                  label="業態"
                  noSelectionLabel="全業態"
                  options={channelOptions || []}
                  value={field.value || []}
                  onChange={field.onChange}
                />
              )}
            />
            <FormHelperText>
              {errors.channels?.message}
            </FormHelperText>
          </FormControl>
          {
            OPTIONAL_FILTERS_BY_SIGNAL_TYPE.genderAges.includes(signalType) ? (
              <FormControl
                fullWidth
                margin="normal"
                error={!!errors.genderAges}
              >
                <Controller
                  name="genderAges"
                  control={control}
                  render={({ field }) => (
                    <AutocompleteMultiSelect
                      label="性別・年代"
                      noSelectionLabel="全性別・年代"
                      options={genderAgeOptions || []}
                      value={field.value || []}
                      onChange={field.onChange}
                    />
                  )}
                />
                <FormHelperText>
                  {errors.genderAges?.message}
                </FormHelperText>
              </FormControl>
            ) : null
          }
          {
            OPTIONAL_FILTERS_BY_SIGNAL_TYPE.shopChains.includes(signalType) ? (
              <FormControl
                fullWidth
                margin="normal"
                error={!!errors.shopChains}
              >
                <Controller
                  name="shopChains"
                  control={control}
                  render={({ field }) => (
                    <AutocompleteMultiSelect
                      label="チェーン"
                      noSelectionLabel="全チェーン"
                      options={iShopChainsData || []}
                      value={field.value || []}
                      onChange={field.onChange}
                    />
                  )}
                />
                <FormHelperText>
                  {errors.shopChains?.message}
                </FormHelperText>
              </FormControl>
            ) : null
          }
          <FormControl
            fullWidth
            margin="normal"
          >
            <Controller
              name="startDate"
              control={control}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
                  <DatePicker
                    {...field}
                    label="開始日"
                    inputFormat="YYYY/MM/DD"
                    mask="____/__/__"
                    minDate={minStartDate}
                    maxDate={dayjs()}
                    onChange={(date) => {
                      const formattedDate = date ? date.format("YYYY-MM-DD") : null;
                      if (formattedDate) {
                        field.onChange(formattedDate);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        fullWidth
                        error={!!errors.startDate}
                        helperText={errors.startDate?.message}
                      />
                    )}
                  />
                </LocalizationProvider>
              )}
            />
          </FormControl>
          <FormControl
            fullWidth
            margin="normal"
            error={!!errors.targetDays}
          >
            <TextField
              {...register("targetDays")}
              variant="standard"
              fullWidth
              label="累計日数"
              error={!!errors.targetDays}
              helperText={errors.targetDays?.message}
            />
          </FormControl>
          <FormControl
            fullWidth
            margin="normal"
            error={!!errors.targetBrand}
          >
            <Controller
              name="targetBrand"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={brandData || []}
                  value={field.value || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={"対象商品"}
                      placeholder={"選択または検索"}
                    />
                  )}
                />
              )}
            />
            <FormHelperText>
              {errors.targetBrand?.message}
            </FormHelperText>
          </FormControl>
          <FormControl
            fullWidth
            margin="normal"
            error={!!errors.comperetiveBrands}
          >
            <Controller
              name="comperetiveBrands"
              control={control}
              render={({ field }) => (
                <AutocompleteMultiSelect
                  label="比較商品 (最大10)"
                  options={brandData || []}
                  value={field.value || []}
                  onChange={field.onChange}
                />
              )}
            />
            <FormHelperText>
              {errors.comperetiveBrands?.message}
            </FormHelperText>
          </FormControl>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: 2, mb: 2 }}
            disabled={isSubmitting}
          >
            閾値設定用 参考値計算
          </Button>
        </form>
      </Box>
      {
        sampleResponse && (
          <NotificationSettingPostForm
            ref={postFormRef}
            condition={{
              id: registeredFormData?.id,
              name: registeredFormData?.name,
              signalType: getValues("signalType"),
              noticeType: registeredFormData?.notice_type,
              active: registeredFormData?.active,
              categories: getValues("categories"),
              areas: getValues("areas") || [],
              channels: getValues("channels") || [],
              genderAges: getValues("genderAges") || [],
              shopChains: getValues("shopChains") || [],
              targetDays: getValues("targetDays"),
              targetBrand: getValues("targetBrand"),
              comperetiveBrands: getValues("comperetiveBrands"),
              minThreshold: registeredFormData?.min_threshold,
              maxThreshold: registeredFormData?.max_threshold,
            }}
            sampleResponse={sampleResponse}
            onClickRecalculate={onClickRecalculate}
          />
        )
      }
    </>
  );
}
